import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  Sidebar as SidebarComponent,
  SidebarContent,
  SidebarHeader,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@bigbinary/neeto-atoms/primitives";
import { isPresent, isNotEmpty } from "neetocist";
import useLocalStorage from "neetocommons/react-utils/useLocalStorage";
import PropTypes from "prop-types";
import { findLast, is } from "ramda";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import useKeyboardShortcutsPaneState from "v2/components/KeyboardShortcuts/hooks/useKeyboardShortcutsPaneState";

import ConfigureView from "./Components/ConfigureView";
import Header from "./Components/Header";
import NavLinks from "./Components/NavLinks";
import {
  SELECTED_NAV_LINK_ROUTE_STORAGE_KEY,
  SIDEBAR_WIDTH_KEY,
  MIN_SIDEBAR_WIDTH,
  MAX_SIDEBAR_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
} from "./constants";
import {
  getActiveConfigurePageLink,
  getSidebarStateLocalStorageKey,
} from "./utils";

const Sidebar = ({
  navLinks,
  moreNavLinks = [],
  customLogo,
  isCountsLoading = false,
  showWorkspaceName = false,
  isExpandable = true,
}) => {
  const [isKeyboardShortcutsPaneOpen] = useKeyboardShortcutsPaneState();

  const [selectedNavLinkRoute, setSelectedNavLinkRoute] = useLocalStorage(
    SELECTED_NAV_LINK_ROUTE_STORAGE_KEY,
    null
  );

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useLocalStorage(
    getSidebarStateLocalStorageKey(),
    false
  );

  const [sidebarWidthRaw, setSidebarWidth] = useLocalStorage(
    SIDEBAR_WIDTH_KEY,
    DEFAULT_SIDEBAR_WIDTH
  );

  const sidebarWidth =
    typeof sidebarWidthRaw === "string"
      ? parseInt(sidebarWidthRaw, 10) || DEFAULT_SIDEBAR_WIDTH
      : sidebarWidthRaw || DEFAULT_SIDEBAR_WIDTH;

  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const startXRef = useRef(null);
  const startWidthRef = useRef(null);

  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const isRTL = document.documentElement.dir === "rtl";
  const sidebarSide = isRTL ? "right" : "left";

  useEffect(() => {
    if (isKeyboardShortcutsPaneOpen) {
      setIsSidebarCollapsed(true);
    } else if (window.innerWidth > 768) {
      setIsSidebarCollapsed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setIsSidebarCollapsed may not be stable across renders
  }, [isKeyboardShortcutsPaneOpen]);

  useEffect(() => {
    if (!isPresent(navLinks)) return;
    const currentLink = findLast(({ to, isActive }) => {
      const url = new URL(to, window.location.href);

      return is(Function, isActive)
        ? isActive()
        : location.pathname.includes(url.pathname);
    })(navLinks);

    if (isPresent(currentLink)) {
      setSelectedNavLinkRoute(
        currentLink.isConfigureNavLink ? currentLink.to : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: sets initial selected route from current URL
  }, []);

  // Drag-to-resize via SidebarRail: intercept mousedown to start drag,
  // update --sidebar-width directly on DOM for instant feedback,
  // persist to localStorage on mouseup.
  const handleRailMouseDown = useCallback(
    e => {
      if (!isExpandable) return;

      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      startXRef.current = e.clientX;
      startWidthRef.current = sidebarWidth;

      const wrapper = document.querySelector("[data-slot='sidebar-wrapper']");
      if (wrapper) wrapper.dataset.resizing = "true";

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      e.preventDefault();
    },
    [sidebarWidth, isExpandable]
  );

  // Prevent SidebarRail's built-in toggle when user dragged to resize
  const handleRailClick = useCallback(e => {
    if (!hasDraggedRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    hasDraggedRef.current = false;
  }, []);

  useEffect(() => {
    if (!isExpandable) return undefined;

    const onMouseMove = e => {
      if (!isDraggingRef.current) return;

      hasDraggedRef.current = true;
      const dx = e.clientX - startXRef.current;
      // In RTL, dragging right shrinks, dragging left expands
      const rtl = document.documentElement.dir === "rtl";
      let newWidth = startWidthRef.current + (rtl ? -dx : dx);
      newWidth = Math.max(
        MIN_SIDEBAR_WIDTH,
        Math.min(newWidth, MAX_SIDEBAR_WIDTH)
      );

      const widthRem = `${newWidth / 16}rem`;
      const wrapper = document.querySelector("[data-slot='sidebar-wrapper']");
      if (wrapper) wrapper.style.setProperty("--sidebar-width", widthRem);

      document.documentElement.style.setProperty(
        "--neeto-molecules-sidebar-width",
        widthRem
      );
    };

    const onMouseUp = e => {
      if (!isDraggingRef.current) return;

      const dx = e.clientX - startXRef.current;
      const rtlUp = document.documentElement.dir === "rtl";
      let finalWidth = startWidthRef.current + (rtlUp ? -dx : dx);
      finalWidth = Math.round(
        Math.max(MIN_SIDEBAR_WIDTH, Math.min(finalWidth, MAX_SIDEBAR_WIDTH))
      );
      setSidebarWidth(finalWidth);

      const wrapper = document.querySelector("[data-slot='sidebar-wrapper']");
      if (wrapper) delete wrapper.dataset.resizing;

      isDraggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isExpandable, setSidebarWidth]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--neeto-molecules-sidebar-width",
      `${sidebarWidth / 16}rem`
    );
  }, [sidebarWidth]);

  const handleGoBack = event => {
    event.preventDefault();
    setSelectedNavLinkRoute(null);
    const configureNavLink = getActiveConfigurePageLink({ navLinks, location });
    if (configureNavLink?.to) {
      history.push(configureNavLink.to);
    }
  };

  const activeLink = useMemo(
    () =>
      navLinks.find(link => {
        if (is(Function, link.isActive)) return link.isActive();
        const url = new URL(link.to, window.location.href);

        return location.pathname.startsWith(url.pathname);
      }),
    [location.pathname, navLinks]
  );

  const isSelectedNavLinkVisible =
    selectedNavLinkRoute &&
    activeLink?.to === selectedNavLinkRoute &&
    isNotEmpty(navLinks);

  return (
    <SidebarProvider
      className="!w-auto"
      open={!isSidebarCollapsed}
      style={{ "--sidebar-width": `${sidebarWidth / 16}rem` }}
      onOpenChange={open => setIsSidebarCollapsed(!open)}
    >
      <SidebarComponent collapsible="offcanvas" side={sidebarSide}>
        <SidebarHeader className="relative p-0">
          <Header
            {...{ customLogo }}
            clearSettingsNav={() => setSelectedNavLinkRoute(null)}
          />
          <SidebarTrigger className="absolute end-2 top-3" />
          {showWorkspaceName && (
            <div className="border-sidebar-border border-b px-4 py-2">
              <h5 className="text-sidebar-foreground mb-1 text-sm font-semibold">
                {t("neetoMolecules.sidebar.myWorkspace")}
              </h5>
              <span className="text-sidebar-foreground/70 truncate text-xs">
                {globalProps.organization.name}
              </span>
            </div>
          )}
        </SidebarHeader>
        <SidebarContent>
          {isSelectedNavLinkVisible ? (
            <ConfigureView {...{ handleGoBack }} navLink={activeLink} />
          ) : (
            <NavLinks
              {...{
                isCountsLoading,
                moreNavLinks,
                navLinks,
                setSelectedNavLinkRoute,
              }}
            />
          )}
        </SidebarContent>
        {isExpandable && (
          <SidebarRail
            onClickCapture={handleRailClick}
            onMouseDown={handleRailMouseDown}
          />
        )}
      </SidebarComponent>
      {isSidebarCollapsed && (
        <SidebarTrigger className="fixed start-2 top-3 z-50" />
      )}
    </SidebarProvider>
  );
};

Sidebar.propTypes = {
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.func,
        PropTypes.object,
      ]),
      label: PropTypes.string,
      to: PropTypes.string,
      onClick: PropTypes.func,
      count: PropTypes.number,
      isActive: PropTypes.func,
      checkpointKey: PropTypes.string,
      isCountsLoading: PropTypes.bool,
      tag: PropTypes.object,
      addActions: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          label: PropTypes.string,
          onClick: PropTypes.func,
        })
      ),
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          to: PropTypes.string,
          onClick: PropTypes.func,
          href: PropTypes.string,
          count: PropTypes.number,
          isActive: PropTypes.func,
          type: PropTypes.string,
          filters: PropTypes.array,
          checkpointKey: PropTypes.string,
          tag: PropTypes.object,
        })
      ),
    })
  ),
  moreNavLinks: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.func,
        PropTypes.object,
      ]),
      label: PropTypes.string,
      to: PropTypes.string,
      onClick: PropTypes.func,
      count: PropTypes.number,
      isActive: PropTypes.func,
      checkpointKey: PropTypes.string,
      isCountsLoading: PropTypes.bool,
      tag: PropTypes.object,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          to: PropTypes.string,
          onClick: PropTypes.func,
          href: PropTypes.string,
          count: PropTypes.number,
          isActive: PropTypes.func,
          type: PropTypes.string,
          filters: PropTypes.array,
          checkpointKey: PropTypes.string,
          tag: PropTypes.object,
        })
      ),
    })
  ),
  customLogo: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  isCountsLoading: PropTypes.bool,
  showWorkspaceName: PropTypes.bool,
  isExpandable: PropTypes.bool,
};

export default Sidebar;
